import type { DataCoverage } from './race';

export type CoverageKind = 'observed' | 'loading' | 'source-gap' | 'unsupported';

export interface CoverageSegment {
  fromMs: number;
  toMs: number;
  kind: CoverageKind;
  reason?: string;
}

export interface CoverageState {
  durationMs: number;
  segments: CoverageSegment[];
}

const loadingReason = (reason: string) => /未载入|尚未载入|loading|unloaded/i.test(reason);

export function coverageFromDataCoverage(durationMs: number, coverage?: DataCoverage): CoverageState {
  const endMs = Math.max(0, durationMs);
  const gaps = (coverage?.gaps ?? []).filter(gap => gap.toMs > gap.fromMs).sort((a, b) => a.fromMs - b.fromMs);
  const segments: CoverageSegment[] = [];
  let cursor = 0;
  for (const gap of gaps) {
    const fromMs = Math.max(0, Math.min(endMs, gap.fromMs));
    const toMs = Math.max(fromMs, Math.min(endMs, gap.toMs));
    if (fromMs > cursor) segments.push({ fromMs: cursor, toMs: fromMs, kind: 'observed' });
    if (toMs > fromMs) segments.push({ fromMs, toMs, kind: loadingReason(gap.reason) ? 'loading' : 'source-gap', reason: gap.reason });
    cursor = Math.max(cursor, toMs);
  }
  if (cursor < endMs) segments.push({ fromMs: cursor, toMs: endMs, kind: 'observed' });
  if (!segments.length && endMs > 0) segments.push({ fromMs: 0, toMs: endMs, kind: 'observed' });
  return { durationMs: endMs, segments };
}

export function addLoadingSegment(state: CoverageState, fromMs: number, toMs: number): CoverageState {
  if (toMs <= fromMs) return state;
  return { ...state, segments: [...state.segments, { fromMs: Math.max(0, fromMs), toMs: Math.min(state.durationMs, toMs), kind: 'loading' }] };
}

export function coverageKindAt(state: CoverageState, atMs: number): CoverageKind {
  return [...state.segments].reverse().find(segment => atMs >= segment.fromMs && atMs < segment.toMs)?.kind ?? 'loading';
}
